export const setUpOutline = () => {
  const progressLine = document.querySelector(
    '.js-outline-progress-line',
  ) as HTMLElement;
  const outlineElements = document.querySelectorAll(
    '[class*="js-outline-element-"]',
  ) as NodeListOf<HTMLElement>;
  const outlineToggle = document.querySelector(
    '.js-outline-toggle',
  ) as HTMLElement;
  const outlineContainer = document.querySelector(
    '.js-outline-container',
  ) as HTMLElement;

  if (
    !progressLine ||
    !outlineElements.length ||
    !outlineToggle ||
    !outlineContainer
  )
    return;

  interface OutlineElementData {
    outlineElement: HTMLElement;
    targetElement: HTMLElement | null;
    verticalPosition: number;
    dotPosition: number;
  }

  const outlineElementData: OutlineElementData[] = [];

  const calculateOutlinePositions = () => {
    outlineElementData.length = 0; // Clear existing data
    outlineElements.forEach((outlineElement) => {
      const targetId = outlineElement.getAttribute('data-target-id');
      const targetElement = targetId ? document.getElementById(targetId) : null;
      const dot = outlineElement.querySelector(
        '.outline--progress-dot',
      ) as HTMLElement;
      const dotPosition = dot
        ? dot.getBoundingClientRect().top + window.scrollY
        : 0;

      outlineElementData.push({
        outlineElement,
        targetElement,
        verticalPosition: targetElement
          ? targetElement.getBoundingClientRect().top + window.scrollY
          : 0,
        dotPosition,
      });
    });
  };

  calculateOutlinePositions();

  // in-document outline headings reference the outer pages of
  // the embedded BuildVu document, which are created somewhere in
  // 4gen.js. scribd_api.js then will fire a 'docReady' event that
  // lets us know that the elements we need are now in the DOM
  window.addEventListener('docReady', () => {
    calculateOutlinePositions();
    updateProgressLinePadding();
  });

  progressLine.style.setProperty('--progress-percentage', '0%');

  const updateVerticalPositions = () => {
    outlineElementData.forEach((element) => {
      if (element.targetElement) {
        element.verticalPosition =
          element.targetElement.getBoundingClientRect().top + window.scrollY;
        const dot = element.outlineElement.querySelector(
          '.outline--progress-dot',
        ) as HTMLElement;
        element.dotPosition = dot
          ? dot.getBoundingClientRect().top + window.scrollY
          : element.dotPosition;
      }
    });
  };

  const handleOutlineToggle = () => {
    const isExpanded = outlineToggle.getAttribute('aria-expanded') === 'true';
    if (isExpanded) {
      outlineToggle.setAttribute('aria-expanded', 'false');
      outlineToggle.setAttribute('aria-label', 'Expand outline');
      outlineContainer.classList.remove('expanded');
      outlineToggle.classList.remove('expanded');
      progressLine.classList.remove('expanded');
    } else {
      outlineToggle.setAttribute('aria-expanded', 'true');
      outlineToggle.setAttribute('aria-label', 'Collapse outline');
      outlineContainer.classList.add('expanded');
      outlineToggle.classList.add('expanded');
      progressLine.classList.add('expanded');
    }
  };

  const collapseOutline = () => {
    if (outlineToggle.getAttribute('aria-expanded') === 'true') {
      handleOutlineToggle();
    }
  };

  // Add click handlers to all outline element links
  outlineElements.forEach((element) => {
    const link = element.querySelector(
      '.outline--element-label',
    ) as HTMLElement;
    if (link) {
      link.addEventListener('click', () => {
        collapseOutline();
      });
    }
  });

  outlineToggle.addEventListener('click', handleOutlineToggle);

  const updateProgress = () => {
    const scrollPosition = window.scrollY;
    const windowHeight = window.innerHeight;
    let nextUnvisitedElementIndex = outlineElementData.length;
    let primaryElementIndex = 0;

    // Updates the styling of the outline elements: progress dots & elements' text
    outlineElementData.forEach((element, index) => {
      if (element.targetElement) {
        const targetRect = element.targetElement.getBoundingClientRect();
        const isScrolledPast = scrollPosition >= element.verticalPosition;
        const isFullyVisible =
          targetRect.top >= 0 && targetRect.bottom <= windowHeight;

        if (isScrolledPast || isFullyVisible) {
          element.outlineElement.classList.add('visited');
          primaryElementIndex = index;
        } else {
          element.outlineElement.classList.remove('visited');
          if (index < nextUnvisitedElementIndex) {
            nextUnvisitedElementIndex = index;
          }
        }
      }
    });

    // Set active class on the primary element
    outlineElementData.forEach((element, index) => {
      if (index === primaryElementIndex) {
        element.outlineElement.classList.add('primary');
      } else {
        element.outlineElement.classList.remove('primary');
      }
    });

    // Calculate progress percentage based on dot positions
    let progressPercentage = 0;
    if (nextUnvisitedElementIndex > 0) {
      const firstDotPosition = outlineElementData[0].dotPosition;
      const lastDotPosition =
        outlineElementData[outlineElementData.length - 1].dotPosition;
      const totalDistance = lastDotPosition - firstDotPosition;

      if (nextUnvisitedElementIndex < outlineElementData.length) {
        const lastVisitedElementIndex = nextUnvisitedElementIndex - 1;
        const currentDotPosition =
          outlineElementData[lastVisitedElementIndex].dotPosition;
        const nextDotPosition =
          outlineElementData[nextUnvisitedElementIndex].dotPosition;

        // Calculate base progress up to last visited dot
        const baseProgress =
          ((currentDotPosition - firstDotPosition) / totalDistance) * 100;

        // Calculate partial progress to next dot
        const distanceBetweenDots = nextDotPosition - currentDotPosition;
        const scrollPastCurrentDot = Math.max(
          0,
          scrollPosition -
            outlineElementData[lastVisitedElementIndex].verticalPosition,
        );
        const distanceBetweenTargets =
          outlineElementData[nextUnvisitedElementIndex].verticalPosition -
          outlineElementData[lastVisitedElementIndex].verticalPosition;
        const progressRatio = scrollPastCurrentDot / distanceBetweenTargets;
        const additionalProgress =
          (distanceBetweenDots / totalDistance) * progressRatio * 100;

        progressPercentage = Math.min(100, baseProgress + additionalProgress);
      } else {
        progressPercentage = 100;
      }
    }

    progressLine.style.setProperty(
      '--progress-percentage',
      `${progressPercentage}%`,
    );
  };

  const updateProgressLinePadding = () => {
    if (outlineElements.length === 0) return;

    const lastElement = outlineElements[outlineElements.length - 1];
    const lastElementHeight = lastElement.offsetHeight;
    const progressContainer = progressLine.parentElement;

    if (progressContainer) {
      progressContainer.style.paddingBottom = `${lastElementHeight - 5}px`;
    }
  };

  window.addEventListener(
    'scroll',
    () => {
      updateVerticalPositions();
      updateProgress();
    },
    { passive: true },
  );

  window.addEventListener(
    'resize',
    () => {
      updateVerticalPositions();
      updateProgress();
      updateProgressLinePadding();
    },
    { passive: true },
  );

  outlineToggle.addEventListener('click', handleOutlineToggle);
  updateProgressLinePadding();
};
